import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { DeviceProvisioningRequest } from '../../models/device-provisioning-request.model';
import { DeviceCapabilityResponse, DeviceHeartbeatResponse, DeviceProvisioningResponse, DeviceStatusResponse, MessageStatusView } from '../../models/device-provisioning-response.model';
import { ApiResponse, PageableApiResponse } from '../../../api-response';
import { BaseApiService } from '../../../../core/base-api.service';

@Injectable({
  providedIn: 'root'
})
export class ProvisioningService extends BaseApiService {
  private readonly http = inject(HttpClient);
  private readonly deviceProvisioningBaseUrl = `${this.coreApiUrl}/api/v1/device`;

  getProvisionedDevices(page: number, pageSize: number, orderColumn: string, orderDirection: string): Observable<PageableApiResponse<DeviceProvisioningResponse>> {
    return this.http.get<PageableApiResponse<DeviceProvisioningResponse>>(
      `${this.deviceProvisioningBaseUrl}?page=${page}&size=${pageSize}&sort=${orderColumn},${orderDirection}`
    );
  }

  /** Single-device fetch by clientId — lets pages that only have a clientId
   *  (e.g. a route param) load the full device DTO themselves instead of
   *  depending on the caller having passed it via router navigation state. */
  getDeviceByClientId(clientId: string): Observable<ApiResponse<DeviceProvisioningResponse>> {
    return this.http.get<ApiResponse<DeviceProvisioningResponse>>(`${this.deviceProvisioningBaseUrl}/${encodeURIComponent(clientId)}`);
  }

  /** Returns the created device DTO (per-endpoint DB write is now
   *  read-your-write consistent) — use it to update local state directly
   *  rather than refetching the list, which is not immediately consistent. */
  postProvisioningForDevice(body: DeviceProvisioningRequest): Observable<ApiResponse<DeviceProvisioningResponse>> {
    return this.http.post<ApiResponse<DeviceProvisioningResponse>>(this.deviceProvisioningBaseUrl, body);
  }

  // TODO: putProvisioningForDevice still returns ApiResponse<boolean> — only
  // POST was confirmed changed. Update this the same way once/if PUT also
  // returns the updated device DTO.
  putProvisioningForDevice(body: DeviceProvisioningRequest): Observable<ApiResponse<boolean>> {
    return this.http.put<ApiResponse<boolean>>(`${this.deviceProvisioningBaseUrl}/${body.clientId}`, body);
  }

  getMqttStatusForDevice(device: DeviceProvisioningResponse): Observable<ApiResponse<void>> {
    return this.http.get<ApiResponse<void>>(
      `${this.deviceProvisioningBaseUrl}/status?clientId=${device.clientId}`
    );
  }

  triggerCapabilityRequest(clientId: string): Observable<ApiResponse<void>> {
    return this.http.get<ApiResponse<void>>(
      `${this.deviceProvisioningBaseUrl}/capability?clientId=${clientId}`
    );
  }

  getStoredCapabilityForDevice(clientId: string): Observable<ApiResponse<DeviceCapabilityResponse>> {
    return this.http.get<ApiResponse<DeviceCapabilityResponse>>(
      `${this.deviceProvisioningBaseUrl}/${clientId}/capability`
    );
  }

  assignOperationProfileToDevice(clientId: string, operationProfileId: number): Observable<ApiResponse<null>> {
    return this.http.post<ApiResponse<null>>(
      `${this.deviceProvisioningBaseUrl}/set-operation-profile`,
      { clientId, operationProfileId }
    );
  }

  getHeartbeatForDevice(clientId: string): Observable<ApiResponse<DeviceHeartbeatResponse>> {
    return this.http.get<ApiResponse<DeviceHeartbeatResponse>>(
      `${this.deviceProvisioningBaseUrl}/${clientId}/heartbeat`
    );
  }

  getStatusForDevice(clientId: string): Observable<ApiResponse<DeviceStatusResponse>> {
    return this.http.get<ApiResponse<DeviceStatusResponse>>(
      `${this.deviceProvisioningBaseUrl}/${clientId}/status`
    );
  }

  getActiveTemplate(clientId: string): Observable<ApiResponse<void>> {
    return this.http.get<ApiResponse<void>>(
      `${this.deviceProvisioningBaseUrl}/get-active-template?clientId=${clientId}`
    );
  }

  getAllTemplates(clientId: string): Observable<ApiResponse<void>> {
    return this.http.get<ApiResponse<void>>(
      `${this.deviceProvisioningBaseUrl}/get-all-templates?clientId=${clientId}`
    );
  }

  getAllTemplateGroups(clientId: string): Observable<ApiResponse<void>> {
    return this.http.get<ApiResponse<void>>(
      `${this.deviceProvisioningBaseUrl}/get-all-template-groups?clientId=${clientId}`
    );
  }

  getRecentMessagesForDevice(clientId: string, limit = 50): Observable<ApiResponse<MessageStatusView[]>> {
    return this.http.get<ApiResponse<MessageStatusView[]>>(
      `${this.deviceProvisioningBaseUrl}/${clientId}/messages?limit=${limit}`
    );
  }

  postSoftwareUpdateForDevice(clientId: string, updateUrl: string, md5Checksum: string): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(
      `${this.deviceProvisioningBaseUrl}/update`,
      { clientId, updateUrl, md5Checksum }
    );
  }

  // RESET TYPES: DATA, FULL, RESTART
  postMqttResetForDevice(device: DeviceProvisioningResponse, resetType: string = 'RESTART'): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(
      `${this.deviceProvisioningBaseUrl}/reset?clientId=${device.clientId}&resetType=${resetType}`,
      {}
    );
  }

  deleteDevice(clientId: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.deviceProvisioningBaseUrl}/${clientId}`);
  }
}
